import { useState, useEffect } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { format, subYears } from 'date-fns';
import { useParams } from 'react-router-dom';
import Menu from '@/components/Menu';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import { useAutofillFromResume } from '@/hooks/useAutofillFromResume';
import candidatesServices, {
  ExperienceParamsType,
  ExperienceResponseType,
  MutateExperienceParams,
} from '@/services/candidates';
import { PeriodType } from '@/trio-ui/DatePeriod/DatePeriod.container';
import { DropdownWithLabelOptionType } from '@/trio-ui/Dropdown/Dropdown.types';
import TransparentButton from '@/trio-ui/TransparentButton';
import { getYearsFromNowList, Months } from '@/utils/dates';
import { yearsOfExperienceOptions } from '@/utils/dropdownOptions';
import DialogBox from '../DialogBox';
import SectionBox from '../SectionBox';
import WorkExperience from '../WorkExperience';
import { WorkExperienceType } from '../WorkExperience.types';

type CandidateProfessionalExperienceContainerProps = {
  techOptions: DropdownWithLabelOptionType[];
  defaultWorkExperiences: WorkExperienceType[];
};

const projectFields: Omit<WorkExperienceType, 'id'> & { id?: string } = {
  title: '',
  company: '',
  startMonth: Months.January,
  startYear: format(subYears(new Date(), 1), 'yyyy'),
  endMonth: Months.January,
  endYear: format(new Date(), 'yyyy'),
  techs: [],
  description: '',
  location: '',
  isCurrentWork: false,
};

const defaultCandidateProject = {
  ...projectFields,
  isEditing: true,
};

type EditingWorkExperience = Omit<typeof defaultCandidateProject, 'isEditing'> & {
  isEditing?: boolean;
};

const CandidateProfessionalExperienceContainer = function ({
  techOptions,
  defaultWorkExperiences,
}: CandidateProfessionalExperienceContainerProps): JSX.Element {
  const { id } = useParams();
  const personId = id ?? '';

  const { extractFromResume, getTechnologieMatch, isLoading } = useAutofillFromResume(
    personId as string
  );

  const toast = useToastContext();

  const [workExperiences, setWorkExperiences] =
    useState<EditingWorkExperience[]>(defaultWorkExperiences);

  const [experienceIndexToDelete, setExperienceIndexToDelete] = useState('');

  const createExperienceMutation = useApiSubmit<ExperienceResponseType, MutateExperienceParams>(
    candidatesServices.addNewExperience
  );

  const updateExperienceMutation = useApiSubmit<ExperienceResponseType, MutateExperienceParams>(
    candidatesServices.updateExperience
  );

  const deleteExperienceMutation = useApiSubmit<null, ExperienceParamsType>(
    candidatesServices.deleteExperience
  );

  useEffect(() => {
    if (defaultWorkExperiences.length) {
      const persistedWorkExperiences = defaultWorkExperiences.map((experience) => ({
        ...experience,
        techs: experience.techs.map((tech) => tech.id),
        isEditing: false,
      }));

      setWorkExperiences(persistedWorkExperiences);
    }
  }, [defaultWorkExperiences]);

  useEffect(() => {
    if (!workExperiences.length) {
      setWorkExperiences([defaultCandidateProject]);
    }
  }, [workExperiences]);

  const onAddNewExperience = (): void => {
    setWorkExperiences((prev) => prev.concat(defaultCandidateProject));
  };

  const onDeleteExperience = async (index: number): Promise<void> => {
    try {
      const currentExperience = workExperiences[index];

      if (currentExperience.id) {
        const params = {
          personId,
          experienceId: currentExperience.id,
        };

        await deleteExperienceMutation.mutateAsync(params);
      }

      setWorkExperiences((prev) => prev.filter((p, i) => i !== index));
      setExperienceIndexToDelete('');

      toast.showSuccessFeedback('The experience has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the experience.');
    }
  };

  const onEditExperience = (index: number): void => {
    setWorkExperiences((prev) =>
      prev.map((project, i) => {
        if (i !== index) {
          return project;
        }
        return {
          ...project,
          isEditing: true,
        };
      })
    );
  };

  const onCancelExperienceEdition = (index: number): void => {
    const currentExperience = workExperiences[index];

    if (currentExperience.id) {
      setWorkExperiences((prev) =>
        prev.map((project, i) => {
          if (i !== index) {
            return project;
          }

          return {
            ...project,
            isEditing: false,
          };
        })
      );
    } else {
      setWorkExperiences((prev) => prev.filter((p, i) => i !== index));
    }
  };

  const getNormalizedExperience = (experience: any): WorkExperienceType => {
    const normalized = {
      ...experience,
      techs: experience?.techs?.map((tech) => tech.id),
    };

    delete normalized.person;
    delete normalized.updatedAt;
    delete normalized.createdAt;

    return normalized;
  };

  const getStartDate = (startMonth: Months, startYear: string | number): PeriodType => ({
    month: startMonth,
    year: startYear,
  });

  const getEndDate = (
    isCurrentWork: boolean,
    endMonth?: Months,
    endYear?: string | number
  ): Partial<PeriodType> => ({
    month: isCurrentWork ? null : endMonth,
    year: isCurrentWork ? null : endYear,
  });

  const getNewExperienceObject = (newExperience: EditingWorkExperience): any => ({
    ...newExperience,
    start: getStartDate(newExperience.startMonth, newExperience.startYear),
    end: getEndDate(newExperience.isCurrentWork, newExperience.endMonth, newExperience.endYear),
  });

  const onSaveExperience = async (values: any, index: number): Promise<void> => {
    try {
      const currentExperience = workExperiences[index];

      const newExperience = {
        ...currentExperience,
        ...values,
      };

      delete newExperience.isEditing;

      const mutationToCall = currentExperience.id
        ? updateExperienceMutation
        : createExperienceMutation;

      const params = {
        personId,
        experienceId: currentExperience.id ?? '',
        workExperience: getNewExperienceObject(newExperience),
      };

      const persistedExperience = await mutationToCall.mutateAsync(params);

      setWorkExperiences((prev) => {
        return prev.map((experience, i) => {
          if (i !== index) {
            return experience;
          }

          return {
            ...getNormalizedExperience(persistedExperience),
            isEditing: false,
          };
        });
      });

      toast.showSuccessFeedback('The experience has been saved.');
    } catch {
      toast.showErrorFeedback('There was a problem to save the experience.');
    }
  };

  const updateIndexToDelete = (index: number): void => {
    setExperienceIndexToDelete(index.toString());
  };

  const isSubmitting = createExperienceMutation.isLoading || updateExperienceMutation.isLoading;

  const handleAutofillFromResume = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();

    try {
      const experiences = await extractFromResume('experience');
      const experienceList =
        typeof experiences === 'string' ? JSON.parse(experiences) : experiences;

      if (!Array.isArray(experienceList)) {
        throw new Error('Experiences data is not in the expected format');
      }

      const formattedExperiences: EditingWorkExperience[] = experienceList
        .filter((experience) => experience && typeof experience === 'object')
        .map((experience) => ({
          company: experience.company,
          description: experience.description,
          endMonth: experience.endMonth,
          endYear:
            experience.endYear === '---' ? experience.endYear : parseInt(experience.endYear, 10),
          isCurrentWork: experience.isCurrentWork,
          location: experience.location,
          startMonth: experience.startMonth,
          startYear:
            experience.startYear === '---'
              ? experience.startYear
              : parseInt(experience.startYear, 10),
          techs: experience.techs.map((tech: string) => getTechnologieMatch(tech)).filter(Boolean),
          title: experience.title,
          isEditing: false,
        }));

      await Promise.all(
        formattedExperiences.map(async (currentExperience) => {
          const params = {
            personId,
            experienceId: '',
            workExperience: getNewExperienceObject(currentExperience),
          };

          const mutationResponse = await createExperienceMutation.mutateAsync(params);
          const persistedExpertise = mutationResponse[0];
          return persistedExpertise;
        })
      );

      setWorkExperiences((prev) => [
        ...prev,
        ...(formattedExperiences.map(getNormalizedExperience) as EditingWorkExperience[]),
      ]);

      toast.showSuccessFeedback('Experiences have been imported from resume.');
    } catch (error) {
      toast.showErrorFeedback(
        'Failed to import experiences from resume. Please check if the resume is a valid PDF file.'
      );
    }
  };

  return (
    <>
      <SectionBox
        id="professional-experience"
        title="Professional Experiences"
        leftElement={
          isLoading ? (
            <Spinner size="xs" />
          ) : (
            <TransparentButton style={{ height: '24px' }} isBlueButton onClick={onAddNewExperience}>
              + Add professional experience
            </TransparentButton>
          )
        }
        rightElement={
          <Menu
            icon={MoreHorizIcon}
            menuId="autofill-professional-experience-from-resume-button"
            menuItems={[
              {
                label: 'Autofill from Resume',
                menuItemIcon: AutoFixHigh,
                callback: handleAutofillFromResume,
              },
            ]}
          />
        }
      >
        <>
          {workExperiences.map((experience, index) => {
            return (
              <WorkExperience
                {...experience}
                isSubmitting={isSubmitting}
                key={index.toString()}
                techOptions={techOptions}
                onDeleteExperience={() => updateIndexToDelete(index)}
                onEditExperience={() => onEditExperience(index)}
                onSaveExperience={(values) => onSaveExperience(values, index)}
                onCancelExperienceEdition={() => onCancelExperienceEdition(index)}
                isEditing={experience.isEditing as boolean}
                techs={experience.techs as string[]}
                yearOptions={getYearsFromNowList()}
              />
            );
          })}

          <TransparentButton
            style={{ marginTop: '24px' }}
            isBlueButton
            onClick={onAddNewExperience}
          >
            + Add professional experience
          </TransparentButton>
        </>
      </SectionBox>

      <DialogBox
        isOpen={!!experienceIndexToDelete}
        onCancelHandler={() => setExperienceIndexToDelete('')}
        onConfirmHandler={() => onDeleteExperience(Number(experienceIndexToDelete))}
      />
    </>
  );
};

export default CandidateProfessionalExperienceContainer;
